import type { ClassData, DeletionRecord } from '../types';

/**
 * Everything on the CLASS DOC that still refers to a student after they're deleted.
 *
 * deleteStudent used to clear the roster, grades, releasedGrades, the private-roster
 * PII and the grade subdocs — but left the student's SUBMISSIONS (their work,
 * teacher feedback, and the private teacher↔student thread), attendance marks,
 * XP events and seat behind on a class doc that classmates can read. That's both an
 * erasure gap and residue nobody expects after "delete".
 *
 * Pure so it's unit-testable; returns only the fields that actually change.
 */
export function purgeStudentResidue(
  classData: Pick<ClassData, 'submissions' | 'attendance' | 'xpEvents' | 'seatingChart'>,
  studentId: string,
): Partial<ClassData> {
  const out: Partial<ClassData> = {};

  // Submissions: drop the student's own; for a groupmate's submission, remove them
  // from the group rather than deleting someone else's work.
  if (classData.submissions?.length) {
    /**
     * Rows covered by a group submission that is about to be deleted must be
     * RELEASED, or they disappear for good.
     *
     * A covered row is hidden from the teacher's list by
     * `visibleSubmissions()`, and the only thing that can un-hide it is a merge
     * targeting the row it points at. Delete the primary student and that row
     * goes with them — so their partners' hand-ins stayed hidden with nothing
     * left able to release them, recoverable only by restoring the deleted
     * student from history.
     */
    const orphanedGroupIds = new Set(
      classData.submissions.filter(s => s.studentId === studentId).map(s => s.id),
    );
    const next = classData.submissions
      .filter(s => s.studentId !== studentId)
      .map(s => {
        if (s.coveredByGroupSubmissionId && orphanedGroupIds.has(s.coveredByGroupSubmissionId)) {
          const { coveredByGroupSubmissionId: _released, ...released } = s;
          return released as typeof s;
        }
        return s;
      })
      .map(s => {
        if (!s.groupMemberIds?.includes(studentId)) return s;
        const idx = s.groupMemberIds.indexOf(studentId);
        const groupMemberIds = s.groupMemberIds.filter(id => id !== studentId);
        // groupMemberNames runs parallel to groupMemberIds — drop the same slot so
        // a deleted student's NAME doesn't linger on someone else's submission.
        // Only do that when the arrays are actually aligned: if they've drifted, a
        // slot-remove would delete SOMEONE ELSE'S name. A stale name is residue;
        // corrupting a classmate's record is worse. Leave names alone in that case.
        const aligned = !!s.groupMemberNames && s.groupMemberNames.length === s.groupMemberIds.length;
        const groupMemberNames = aligned
          ? s.groupMemberNames!.filter((_, i) => i !== idx)
          : s.groupMemberNames;
        return { ...s, groupMemberIds, ...(groupMemberNames ? { groupMemberNames } : {}) };
      });
    out.submissions = next;
  }

  // Attendance: the student's mark, late time and note on every day.
  if (classData.attendance?.length) {
    let touched = false;
    const next = classData.attendance.map(day => {
      const inDay = day.records?.[studentId] !== undefined
        || day.lateTimes?.[studentId] !== undefined
        || day.notes?.[studentId] !== undefined;
      if (!inDay) return day;
      touched = true;
      const copy = { ...day, records: { ...day.records } };
      delete copy.records[studentId];
      if (day.lateTimes) { copy.lateTimes = { ...day.lateTimes }; delete copy.lateTimes[studentId]; }
      if (day.notes) { copy.notes = { ...day.notes }; delete copy.notes[studentId]; }
      return copy;
    });
    if (touched) out.attendance = next;
  }

  // Gamification history.
  if (classData.xpEvents?.length) {
    const next = classData.xpEvents.filter(e => e.studentId !== studentId);
    if (next.length !== classData.xpEvents.length) out.xpEvents = next;
  }

  // Seat (both layout modes).
  if (classData.seatingChart) {
    const sc = classData.seatingChart;
    const inPositions = sc.positions && studentId in sc.positions;
    const inFreeform = sc.freeformPositions && studentId in sc.freeformPositions;
    if (inPositions || inFreeform) {
      const nextChart = { ...sc };
      if (inPositions) { nextChart.positions = { ...sc.positions }; delete nextChart.positions[studentId]; }
      if (inFreeform) { nextChart.freeformPositions = { ...sc.freeformPositions }; delete nextChart.freeformPositions![studentId]; }
      out.seatingChart = nextChart;
    }
  }

  return out;
}

/**
 * The INVERSE of purgeStudentResidue: merge a restored student's snapshotted residue
 * back into a class doc's arrays/maps. Append-only with by-id dedupe
 * (submissions/xpEvents), per-date + per-student merge (attendance), and per-student
 * seat keys — so it NEVER clobbers other students' data and is safe to re-run
 * (idempotent, for the restore retry loop). Pure; restoreStudentResidue does the I/O.
 */
type StudentResidue = NonNullable<DeletionRecord['studentResidue']>;

export function computeResidueUpdates(
  data: Pick<ClassData, 'submissions' | 'attendance' | 'xpEvents' | 'seatingChart'>,
  studentId: string,
  residue: StudentResidue,
): Partial<ClassData> {
  const updates: Partial<ClassData> = {};

  // Submissions: append the student's own submissions that aren't already present.
  if (residue.submissions?.length) {
    const existing = new Set((data.submissions || []).map(s => s.id));
    const toAdd = residue.submissions.filter(s => !existing.has(s.id));
    if (toAdd.length) updates.submissions = [...(data.submissions || []), ...toAdd];
  }

  // Attendance: re-insert this student's per-day mark/lateTime/note into existing days.
  if (residue.attendance?.length) {
    const byDate = new Map(residue.attendance.map(a => [a.date, a]));
    let touched = false;
    const nextDays = (data.attendance || []).map(day => {
      const a = byDate.get(day.date);
      if (!a) return day;
      touched = true;
      const copy = { ...day, records: { ...day.records } };
      if (a.record) copy.records[studentId] = a.record;
      if (a.lateTime) { copy.lateTimes = { ...(day.lateTimes || {}) }; copy.lateTimes[studentId] = a.lateTime; }
      if (a.note) { copy.notes = { ...(day.notes || {}) }; copy.notes[studentId] = a.note; }
      return copy;
    });
    if (touched) updates.attendance = nextDays;
  }

  // XP events: append those not already present (by id).
  if (residue.xpEvents?.length) {
    const existing = new Set((data.xpEvents || []).map(e => e.id));
    const toAdd = residue.xpEvents.filter(e => !existing.has(e.id));
    if (toAdd.length) updates.xpEvents = [...(data.xpEvents || []), ...toAdd];
  }

  // Seat position (only if the class still has a seating chart).
  if (residue.seat && data.seatingChart) {
    const sc = { ...data.seatingChart };
    if (residue.seat.position !== undefined) { sc.positions = { ...(sc.positions || {}) }; sc.positions[studentId] = residue.seat.position; }
    if (residue.seat.freeform !== undefined) { sc.freeformPositions = { ...(sc.freeformPositions || {}) }; sc.freeformPositions[studentId] = residue.seat.freeform; }
    if (residue.seat.seat !== undefined) { sc.seats = { ...(sc.seats || {}) }; sc.seats[studentId] = residue.seat.seat; }
    updates.seatingChart = sc;
  }

  return updates;
}
